<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <!-- Backdrop -->
      <div
        v-if="modelValue"
        class="base-modal-backdrop"
        @click.self="closeOnBackdrop && $emit('update:modelValue', false)"
        aria-modal="true"
        role="dialog"
      >
        <!-- Modal panel -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-4 scale-95"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0 scale-100"
          leave-to-class="opacity-0 translate-y-4 scale-95"
        >
          <div v-if="modelValue" class="base-modal-panel" :class="[sizeClass, panelClass]">
            <!-- Header -->
            <div v-if="$slots.header || title" class="base-modal-header">
              <h3 v-if="title" class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ title }}</h3>
              <slot name="header" />

              <!-- Close button -->
              <button
                v-if="showClose"
                type="button"
                class="base-modal-close"
                @click="$emit('update:modelValue', false)"
              >
                <span class="sr-only">Close</span>
                <X class="h-5 w-5" />
              </button>
            </div>

            <!-- Body -->
            <div class="base-modal-body" :class="bodyClass">
              <slot />
            </div>

            <!-- Footer -->
            <div v-if="$slots.footer" class="base-modal-footer">
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { X } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  showClose?: boolean
  closeOnBackdrop?: boolean
  panelClass?: string
  bodyClass?: string
}>(), {
  size: 'md',
  showClose: true,
  closeOnBackdrop: true
})

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const sizeClass = computed(() => {
  const sizes: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl'
  }
  return sizes[props.size] || sizes.md
})
</script>

<style scoped>
.base-modal-backdrop {
  @apply fixed inset-0 z-50
         flex items-center justify-center
         p-4 sm:p-6
         bg-black/50 dark:bg-black/70;
}

.base-modal-panel {
  @apply w-full
         bg-white dark:bg-gray-800
         rounded-2xl
         shadow-xl dark:shadow-2xl
         border border-gray-200 dark:border-gray-700
         flex flex-col
         max-h-[calc(100vh-3rem)];
}

.base-modal-header {
  @apply flex items-center justify-between
         px-4 sm:px-6 py-4
         border-b border-gray-200 dark:border-gray-700
         flex-shrink-0;
}

.base-modal-body {
  @apply px-4 sm:px-6 py-5
         overflow-y-auto
         flex-1;
}

.base-modal-footer {
  @apply flex flex-col-reverse sm:flex-row
         items-stretch sm:items-center
         justify-start sm:justify-end
         gap-2 sm:gap-3
         px-4 sm:px-6 py-4
         border-t border-gray-200 dark:border-gray-700
         flex-shrink-0;
}

.base-modal-footer > :deep(*) {
  @apply w-full sm:w-auto;
}

.base-modal-close {
  @apply p-1.5 -mr-1.5 rounded-lg
         text-gray-400 dark:text-gray-500
         hover:text-gray-600 dark:hover:text-gray-300
         hover:bg-gray-100 dark:hover:bg-gray-700
         transition-colors duration-150;
}
</style>
